import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Trash2, Copy, Download, FileText, Share2, Smartphone, LayoutList,
  Clock, MapPin, User, ChevronUp, ChevronDown, Check, Play, AlarmClock,
} from "lucide-react";

const STATUSES = {
  info: { label: "Information", cls: "bg-stone-100 text-stone-600" },
  to_confirm: { label: "À confirmer", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Validé", cls: "bg-sage/15 text-sage" },
  fixed: { label: "Horaire fixe", cls: "bg-blue-100 text-blue-700" },
  in_progress: { label: "En cours", cls: "bg-champagne/25 text-stone-800" },
  done: { label: "Terminé", cls: "bg-green-100 text-green-700" },
  delayed: { label: "Retardé", cls: "bg-destructive/10 text-destructive" },
};

export default function WeddingDay() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [days, setDays] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("plan"); // plan | dayof
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/weddings/${wid}/day`);
    setDays(data);
    const c = await api.get(`/weddings/${wid}/day/contacts`);
    setContacts(c.data);
  };
  useEffect(() => { if (wid) load(); }, [wid]);

  if (!days) return <Loader />;
  const day = days[activeDay] || days[0];

  const addItem = async () => {
    await api.post(`/weddings/${wid}/day/days/${day.id}/items`, { start_time: "12:00", title: "Nouvel événement" });
    load();
  };
  const patchItem = async (iid, patch) => {
    setDays((prev) => prev.map((d) => ({ ...d, items: d.items.map((i) => (i.id === iid ? { ...i, ...patch } : i)) })));
    await api.patch(`/weddings/${wid}/day/items/${iid}`, patch);
  };
  const delItem = async (iid) => { await api.delete(`/weddings/${wid}/day/items/${iid}`); load(); };
  const dupItem = async (iid) => { await api.post(`/weddings/${wid}/day/items/${iid}/duplicate`); load(); };
  const move = async (idx, dir) => {
    const items = [...day.items];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    setDays((prev) => prev.map((d, x) => (x === activeDay ? { ...d, items } : d)));
    await api.post(`/weddings/${wid}/day/reorder`, { order: items.map((i) => i.id) });
  };
  const addDay = async () => { await api.post(`/weddings/${wid}/day/days`, { label: "Nouveau jour" }); load(); };
  const dl = (path, name) => {
    const token = localStorage.getItem("token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const u = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
      }).catch(() => toast.error("Export impossible"));
  };

  if (mode === "dayof") return <DayOfView days={days} onBack={() => setMode("plan")} onPatch={patchItem} />;

  return (
    <div>
      <PageHeader eyebrow="Jour J" title="Planning de la journée"
        description="Créez votre déroulé complet, minute par minute, et partagez-le avec vos proches et prestataires."
        actions={<>
          <Button variant="outline" onClick={() => setMode("dayof")} data-testid="dayof-mode"><Smartphone className="w-4 h-4 mr-1" /> Vue Jour J</Button>
          <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="share-open"><Share2 className="w-4 h-4 mr-1" /> Partager</Button>
          <Button variant="outline" onClick={() => dl(`/weddings/${wid}/day/export/master`, "planning-jour-j.pdf")} data-testid="export-master"><Download className="w-4 h-4 mr-1" /> PDF complet</Button>
          <Button onClick={addItem} className="rounded-full" data-testid="add-day-item"><Plus className="w-4 h-4 mr-1" /> Événement</Button>
        </>} />

      {/* Day tabs (multi-day) */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {days.map((d, i) => (
          <button key={d.id} onClick={() => setActiveDay(i)} data-testid={`day-tab-${i}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${i === activeDay ? "bg-stone-900 text-white" : "bg-white"}`}>
            {d.label}{d.date ? ` · ${d.date}` : ""}</button>
        ))}
        <button onClick={addDay} className="px-3 py-1.5 rounded-full text-sm border border-dashed text-stone-500" data-testid="add-day"><Plus className="w-3.5 h-3.5 inline" /> Jour</button>
      </div>

      {/* Timeline */}
      <div className="space-y-2" data-testid="day-timeline">
        {day.items.map((it, idx) => (
          <div key={it.id} className="card-lux p-4" data-testid={`day-item-${it.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center pt-1">
                <button onClick={() => move(idx, -1)} className="text-stone-300 hover:text-stone-700"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} className="text-stone-300 hover:text-stone-700"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <Input type="time" value={it.start_time} onChange={(e) => patchItem(it.id, { start_time: e.target.value })}
                className="h-9 w-24 shrink-0 font-mono" data-testid={`item-time-${it.id}`} />
              <div className="flex-1 min-w-0">
                <Input value={it.title} onChange={(e) => patchItem(it.id, { title: e.target.value })}
                  className="h-9 font-medium border-0 px-0 shadow-none focus-visible:ring-0" data-testid={`item-title-${it.id}`} />
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className="inline-flex items-center gap-1 text-xs text-stone-400"><MapPin className="w-3 h-3" />
                    <input value={it.location || ""} onChange={(e) => patchItem(it.id, { location: e.target.value })}
                      placeholder="Lieu" className="bg-transparent outline-none w-28" /></span>
                  <select value={it.responsible_id || ""} data-testid={`item-resp-${it.id}`}
                    onChange={(e) => {
                      const c = contacts.find((x) => x.id === e.target.value);
                      patchItem(it.id, { responsible_id: e.target.value || null, responsible_name: c ? c.name : "" });
                    }}
                    className="text-xs border rounded-full px-2 py-0.5 bg-white">
                    <option value="">Responsable…</option>
                    {contacts.map((c) => <option key={c.id + c.type} value={c.id}>{c.name} ({c.role})</option>)}
                  </select>
                  <select value={it.status} onChange={(e) => patchItem(it.id, { status: e.target.value })}
                    data-testid={`item-status-${it.id}`} className={`text-xs rounded-full px-2 py-0.5 border-0 ${STATUSES[it.status]?.cls || ""}`}>
                    {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <label className="text-xs text-stone-500 flex items-center gap-1">
                    <input type="checkbox" checked={it.fixed_time || false} onChange={(e) => patchItem(it.id, { fixed_time: e.target.checked })} /> Horaire fixe</label>
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-champagne cursor-pointer">Détails, notes & contact</summary>
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    <Textarea value={it.notes || ""} onChange={(e) => patchItem(it.id, { notes: e.target.value })} rows={2} placeholder="À ne pas oublier…" className="text-sm" />
                    <div className="space-y-2">
                      <Input value={it.contact_phone || ""} onChange={(e) => patchItem(it.id, { contact_phone: e.target.value })} placeholder="Téléphone contact" className="h-9" />
                      <select value={it.vendor_id || ""} onChange={(e) => {
                        const c = contacts.find((x) => x.id === e.target.value && x.type === "vendor");
                        patchItem(it.id, { vendor_id: e.target.value || null, vendor_name: c ? c.name : "" });
                      }} className="h-9 w-full text-sm border rounded-md px-2 bg-white">
                        <option value="">Prestataire lié…</option>
                        {contacts.filter((c) => c.type === "vendor").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                </details>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => dupItem(it.id)} className="text-stone-300 hover:text-stone-700" title="Dupliquer"><Copy className="w-4 h-4" /></button>
                <button onClick={() => delItem(it.id)} className="text-stone-300 hover:text-destructive" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {day.items.length === 0 && <div className="text-sm text-stone-400 text-center py-8">Aucun événement. Ajoutez-en un pour commencer.</div>}
      </div>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} wid={wid} contacts={contacts} dl={dl} />
    </div>
  );
}

function ShareDialog({ open, onOpenChange, wid, contacts, dl }) {
  const people = contacts.filter((c) => c.type !== "guest" || true);
  const vendors = contacts.filter((c) => c.type === "vendor");
  const share = async (pid) => {
    const { data } = await api.post(`/weddings/${wid}/day/share/${pid}`);
    const url = `${window.location.origin}/jour-j/${data.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien personnel copié");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Partager les missions du Jour J</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Générez une fiche de mission personnelle (PDF ou lien sécurisé) pour chaque responsable, ou un planning par prestataire.</p>
        <div className="max-h-72 overflow-y-auto divide-y">
          {people.slice(0, 60).map((c) => (
            <div key={c.id + c.type} className="flex items-center gap-2 py-2 text-sm" data-testid={`share-person-${c.id}`}>
              <span className="flex-1">{c.name} <span className="text-xs text-stone-400">({c.role})</span></span>
              <button onClick={() => dl(`/weddings/${wid}/day/export/person/${c.id}`, `planning-${c.name}.pdf`)} className="text-stone-500 hover:text-stone-900" title="PDF mission"><FileText className="w-4 h-4" /></button>
              <button onClick={() => share(c.id)} className="text-stone-500 hover:text-stone-900" title="Lien sécurisé"><Share2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {vendors.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">Plannings prestataires</div>
            {vendors.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-1.5 text-sm">
                <span className="flex-1">{c.name}</span>
                <button onClick={() => dl(`/weddings/${wid}/day/export/vendor/${c.id}`, `planning-${c.name}.pdf`)} className="text-stone-500 hover:text-stone-900"><Download className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DayOfView({ days, onBack, onPatch }) {
  const items = days.flatMap((d) => d.items).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const now = new Date().toTimeString().slice(0, 5);
  const currentIdx = Math.max(0, items.findIndex((i) => (i.start_time || "") >= now));
  return (
    <div className="max-w-md mx-auto" data-testid="dayof-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-2xl">Vue Jour J</div>
        <Button variant="outline" size="sm" onClick={onBack}><LayoutList className="w-4 h-4 mr-1" /> Éditer</Button>
      </div>
      <div className="space-y-3">
        {items.map((it, idx) => {
          const isCurrent = idx === currentIdx;
          return (
            <div key={it.id} className={`rounded-2xl p-4 border ${isCurrent ? "bg-stone-900 text-white border-stone-900" : "bg-white"} ${it.status === "delayed" ? "ring-2 ring-destructive" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg">{it.start_time}</span>
                {isCurrent && <span className="text-xs px-2 py-0.5 rounded-full bg-champagne text-stone-900">Maintenant</span>}
                <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${isCurrent ? "bg-white/20" : STATUSES[it.status]?.cls}`}>{STATUSES[it.status]?.label}</span>
              </div>
              <div className="text-lg font-medium mt-1">{it.title}</div>
              {it.location && <div className={`text-sm flex items-center gap-1 ${isCurrent ? "text-white/70" : "text-stone-400"}`}><MapPin className="w-3.5 h-3.5" /> {it.location}</div>}
              {it.responsible_name && <div className={`text-sm flex items-center gap-1 ${isCurrent ? "text-white/70" : "text-stone-500"}`}><User className="w-3.5 h-3.5" /> {it.responsible_name}{it.contact_phone && <a href={`tel:${it.contact_phone}`} className="underline ml-1">{it.contact_phone}</a>}</div>}
              {it.notes && <div className={`text-sm mt-1 ${isCurrent ? "text-white/80" : "text-stone-600"}`}>📌 {it.notes}</div>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => onPatch(it.id, { status: "in_progress" })} className={`flex-1 text-xs py-2 rounded-lg ${isCurrent ? "bg-white/20" : "bg-stone-100"}`}><Play className="w-3.5 h-3.5 inline" /> Démarrer</button>
                <button onClick={() => onPatch(it.id, { status: "done" })} className={`flex-1 text-xs py-2 rounded-lg ${isCurrent ? "bg-white/20" : "bg-stone-100"}`}><Check className="w-3.5 h-3.5 inline" /> Terminé</button>
                <button onClick={() => onPatch(it.id, { status: "delayed" })} className={`flex-1 text-xs py-2 rounded-lg ${isCurrent ? "bg-white/20" : "bg-stone-100"}`}><AlarmClock className="w-3.5 h-3.5 inline" /> Retard</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
